import { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Download } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { PageTitle } from "./Controls/PageTitle";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { Card, Col, Container, Row, Button as BsButton } from "react-bootstrap";
import { Facility, PlanDetails } from "./Data/ApiTypes";
import { connectUrl } from "./Data/ApiUrl";

type DownloadFormat = "svg" | "png";

interface QRCodeRef extends HTMLDivElement {
    querySelector( selectors: string ): SVGElement | null;
}

export function useParamIds() {
  const params = useParams();

  return {
    facilityId: Number(params.facilityId),
    rateProgramId: params.rateProgramId,
    isValidation: params.isValidation === "true",
    isDiscountValidationType: params.isDiscountValidationType === "true"
  };
}

export function FacilityRateSignPage() {
    const { facilityId, rateProgramId, isValidation, isDiscountValidationType } = useParamIds();
    const nav = useNavigate();
    const facility = useLoaderData() as Facility;
    // if rateProgramId exists, this is specific to an event "flat rate".
    const qrCodeUrl = (isDiscountValidationType) ? `/facility/${facilityId}/validation/redeem/${rateProgramId}` : (isValidation) ? `/facility/${facilityId}/validation/${rateProgramId}`:
     (rateProgramId) ? `/facility/${facilityId}/event/${rateProgramId}` : `/results/${facilityId}`;

    const [text, setText] = useState<string>( connectUrl( qrCodeUrl ) );
    const qrRef = useRef<QRCodeRef>( null );

    const downloadQR = ( format: DownloadFormat ): void => {
        const svg = qrRef.current?.querySelector( "svg" );
        if( !svg ) { return; }
        if( format === "svg" ) {
            const svgData = new XMLSerializer().serializeToString( svg );
            const blob    = new Blob( [svgData], { type: "image/svg+xml" } );
            const url     = URL.createObjectURL( blob );
            downloadFile( url, `qrcode.${format}` );
            URL.revokeObjectURL( url );
        } else {
            const canvas  = document.createElement( "canvas" );
            const ctx     = canvas.getContext( "2d" );
            const img     = new Image();
            const svgBlob = new Blob( [new XMLSerializer().serializeToString( svg )], { type: "image/svg+xml" } );
            const url     = URL.createObjectURL( svgBlob );

            img.onload = (): void => {
                canvas.width  = img.width;
                canvas.height = img.height;
                ctx?.drawImage( img, 0, 0 );
                const imgURI  = canvas.toDataURL( "image/png" );
                downloadFile( imgURI, "qrcode.png" );
                URL.revokeObjectURL( url );
            };
            img.src = url;
        }
    };

    const downloadFile = ( url: string, filename: string ): void => {
        const link    = document.createElement( "a" );
        link.href     = url;
        link.download = filename;
        document.body.appendChild( link );
        link.click();
        document.body.removeChild( link );
    };

    return <Container fluid className="px-0 px-sm-3 py-2">
         <div className="d-flex align-items-center gap-2 px-3 px-sm-0">
         <BsButton variant="link" className="p-0" onClick={() => nav(-1)}>
                ← Back
            </BsButton>
        </div>
        <div className="d-flex align-items-center gap-2 px-3 px-sm-0 mb-3">
           
            <PageTitle className="mb-0">
                {facility.name} {isValidation ? 'Validation' : rateProgramId ? 'Event' : 'Hourly'} Sign
            </PageTitle>
        </div>

        <div className="d-flex flex-column align-items-left gap-3">
            <div ref={qrRef} style={{ width: "min(90vw, 480px)", height: "min(90vw, 480px)" }}>
                <QRCodeSVG
                    width="100%"
                    height="100%"
                    value={text}
                    level="M"
                    includeMargin={true} />
            </div>

            <Card className="text-center" style={{ width: "min(90vw, 480px)" }}>
                <Card.Body>
                    <a target="_blank" href={text} style={{ wordBreak: "break-all" }}>
                        {text}
                    </a>
                </Card.Body>
            </Card>

            <div className="d-flex gap-3 d-print-none" style={{ width: "min(90vw, 480px)" }}>
                <Button className="w-100" onClick={() => downloadQR("svg")}>
                    <Download className="me-2" />
                    Download SVG
                </Button>
                <Button className="border-success w-100" onClick={() => downloadQR("png")}>
                    <Download className="me-2" />
                    Download PNG
                </Button>
            </div>
        </div>
    </Container>;
}